import React from 'react';

import { CONFIG } from 'config';
import { ContextGlobal as CG } from 'contexts';
import * as Page from 'components/page';

import { ContratoCard } from './ContratoCard';
import * as Styled from './StyledContratos';

function Collapsible({ title, count, items, t, defaultOpen }) {
    const [open, setOpen] = React.useState(Boolean(defaultOpen));
    return (
        <Styled.Section>
            <Styled.SectionHead
                as="button"
                type="button"
                collapsible
                open={open}
                onClick={() => setOpen(o => !o)}
            >
                <h3>{title}</h3>
                <Styled.Badge>{count}</Styled.Badge>
                <span className="chevron fas fa-chevron-down" aria-hidden="true" />
            </Styled.SectionHead>
            {open &&
                (items.length ? (
                    items.map(c => <ContratoCard key={c._id} t={t} data={c} readOnly />)
                ) : (
                    <Styled.Empty>{t('panel.empty')}</Styled.Empty>
                ))}
        </Styled.Section>
    );
}

export function ContratosPanel({ t, cv, onExpired }) {
    const { emitToast, emitAlert } = React.useContext(CG);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(false);
    const [data, setData] = React.useState(null);

    const load = React.useCallback(() => {
        setLoading(true);
        setError(false);
        cv.getContracts()
            .then(res => {
                setData(res);
                setLoading(false);
            })
            .catch(err => {
                setLoading(false);
                if (err?.response?.status === 401) {
                    onExpired();
                    return;
                }
                setError(true);
            });
    }, [cv, onExpired]);

    React.useEffect(() => {
        load();
    }, []);

    const onAction = (contract, status) =>
        (contract.kind === 'ecosystem'
            ? cv.validateEcosystemRelationship({ _id: contract._id, status })
            : cv.validateContract({ _id: contract._id, status }))
            .then(() => {
                emitToast(
                    status === 'confirmed'
                        ? t(
                              contract.kind === 'ecosystem'
                                  ? 'panel.relationshipConfirmedToast'
                                  : 'panel.confirmedToast'
                          )
                        : t(
                              contract.kind === 'ecosystem'
                                  ? 'panel.relationshipRejectedToast'
                                  : 'panel.rejectedToast'
                          )
                );
                load();
            })
            .catch(err => {
                if (err?.response?.status === 401) {
                    onExpired();
                    return;
                }
                emitAlert(t('errors.generic'));
            });

    if (loading) return <Styled.Loading>{t('panel.loading')}</Styled.Loading>;
    if (error) {
        return (
            <Styled.ErrorBox>
                <p>{t('panel.error')}</p>
                <button type="button" onClick={load}>
                    {t('panel.retry')}
                </button>
            </Styled.ErrorBox>
        );
    }

    const c = (data && data.contracts) || { pending: [], confirmed: [], rejected: [] };
    const pending = (c.pending || []).map(item => ({ ...item, onAction }));
    const e = (data && data.ecosystem) || { pending: [], confirmed: [], rejected: [] };
    const ecosystemPending = (e.pending || []).map(item => ({ ...item, onAction }));

    return (
        <>
            <Page.Title>{t('panel.title')}</Page.Title>
            <Page.Descr>{t('panel.descr', { email: cv.email })}</Page.Descr>

            <Styled.Section>
                <Styled.SectionHead>
                    <h3>{t('panel.pendingTitle')}</h3>
                    <Styled.Badge alert>{pending.length}</Styled.Badge>
                </Styled.SectionHead>
                {pending.length ? (
                    pending.map(item => <ContratoCard key={item._id} t={t} data={item} />)
                ) : (
                    <Styled.Empty>{t('panel.noPending')}</Styled.Empty>
                )}
            </Styled.Section>

            <Collapsible
                t={t}
                title={t('panel.confirmedTitle')}
                count={(c.confirmed || []).length}
                items={c.confirmed || []}
            />
            <Collapsible
                t={t}
                title={t('panel.rejectedTitle')}
                count={(c.rejected || []).length}
                items={c.rejected || []}
            />

            <Styled.Section>
                <Styled.SectionHead>
                    <h3>{t('panel.ecosystemTitle')}</h3>
                    <Styled.Badge alert>{ecosystemPending.length}</Styled.Badge>
                </Styled.SectionHead>
                {ecosystemPending.length ? (
                    ecosystemPending.map(item => (
                        <ContratoCard key={item._id} t={t} data={item} />
                    ))
                ) : (
                    <Styled.Empty>{t('panel.ecosystemEmpty')}</Styled.Empty>
                )}
            </Styled.Section>

            <Collapsible
                t={t}
                title={t('panel.ecosystemConfirmedTitle')}
                count={(e.confirmed || []).length}
                items={e.confirmed || []}
            />
            <Collapsible
                t={t}
                title={t('panel.ecosystemRejectedTitle')}
                count={(e.rejected || []).length}
                items={e.rejected || []}
            />

            <Styled.RegisterBtn href={`${CONFIG.baseUrl}/ranking`} target="_blank" rel="noopener noreferrer">
                {t('panel.registerButton')}
            </Styled.RegisterBtn>
        </>
    );
}
export default ContratosPanel;
